import React, { useEffect, useMemo, useState } from 'react';

import classnames from 'classnames/bind';

import { AdminVendor, useGetAllVendors, useDeleteVendor } from '@SERVICES';

import { notify } from '@NOTIFICATION/Notificator';

import useNewVendorModal from '@HOOKS/store/modals/useNewVendorModal';
import useDeleteVendorModal from '@HOOKS/store/modals/useDeleteVendorModal';
import useImportVendorsModal from '@HOOKS/store/modals/useImportVendorsModal';

import VendorsTable from '@PAGES/admin/vendors/components/VendorsTable';
import EditVendorModal from '@MODALS/EditVendorModal';
import ImportVendorsModal from '../../../MODALS/ImportVendorsModal';

import styles from './Vendors.module.scss';

const cx: CX = classnames.bind(styles);

function VendorsPage() {
    const {
        data: vendorsData, isLoading, isError, error, refetch,
    } = useGetAllVendors();

    const {
        actions: newVendorModalActions,
    } = useNewVendorModal();

    const {
        actions: deleteVendorModalActions,
    } = useDeleteVendorModal();

    const {
        state: importVendorsModalState,
        actions: importVendorsModalActions,
    } = useImportVendorsModal();
    


    const [searchValue, setSearchValue] = useState<string>('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');
    const [editingVendor, setEditingVendor] = useState<AdminVendor | null>(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [searchValue]);

    // Filter vendors based on search
    const filteredVendors = useMemo(() => {
        if (!vendorsData?.data) return [];
        
        if (!debouncedSearchValue.trim()) {
            return vendorsData.data;
        }

        const searchTerm = debouncedSearchValue.toLowerCase();
        return vendorsData.data.filter((vendor) => vendor.name.toLowerCase().includes(searchTerm)
            || vendor.company.toLowerCase().includes(searchTerm)
            || ((vendor as any).contactPerson && (vendor as any).contactPerson.toLowerCase().includes(searchTerm))
            || vendor.email.toLowerCase().includes(searchTerm)
            || vendor.phone.toLowerCase().includes(searchTerm)
            || vendor.address.toLowerCase().includes(searchTerm)
            || ((vendor as any).website && (vendor as any).website.toLowerCase().includes(searchTerm))
            || ((vendor as any).notes && (vendor as any).notes.toLowerCase().includes(searchTerm)));
    }, [vendorsData?.data, debouncedSearchValue]);

    // Cleanup modals on unmount
    useEffect(() => () => {
        newVendorModalActions.close();
        deleteVendorModalActions.close();
        importVendorsModalActions.close();
        setEditingVendor(null);
    }, [newVendorModalActions, deleteVendorModalActions, importVendorsModalActions]);

    const handleCreateVendor = () => {
        newVendorModalActions.open();
    };

    const handleImportVendors = () => {
        importVendorsModalActions.open();
    };

    const handleEditVendor = (vendor: AdminVendor) => {
        setEditingVendor(vendor);
    };

    const handleDeleteVendor = (vendor: AdminVendor) => {
        deleteVendorModalActions.open({
            id: vendor.id,
            name: vendor.name,
        });
    };

    const handleCloseEditModal = () => {
        setEditingVendor(null);
    };

    const handleImportSuccess = () => {
        // Refresh vendors list after successful import
        refetch();
    };

    const handleCloseImportModal = () => {
        importVendorsModalActions.close();
    };

    if (isError) {
        return (
            <div className={cx('page')}>
                <div className="max-constraint center">
                    <div className={cx('error-state')}>
                        <div className={cx('error-icon')}>⚠️</div>
                        <h2 className={cx('error-title')}>Failed to Load Vendors</h2>
                        <p className={cx('error-message')}>
                            Unable to load vendors. Please check your connection and try again.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cx('page')}>
            <div className="container max-constraint center">
                <div className={cx('page-header')}>
                    <div className={cx('header-content')}>
                        <h1 className={cx('page-title')}>Vendors Management</h1>
                        <p className={cx('page-subtitle')}>
                            Manage your vendor database and contact information
                        </p>
                    </div>
                </div>

                <div className={cx('content-section')}>
                    {/* Vendors Table */}
                    <div className={cx('table-section')}>
                        <VendorsTable
                            vendors={filteredVendors}
                            isLoading={isLoading}
                            onEdit={handleEditVendor}
                            onCreate={handleCreateVendor}
                            onImport={handleImportVendors}
                            onDelete={handleDeleteVendor}
                            searchValue={searchValue}
                            onSearchChange={setSearchValue}
                            searchResultsCount={filteredVendors.length}
                            totalVendorsCount={vendorsData?.data?.length || 0}
                        />
                    </div>
                </div>

                {/* Edit Vendor Modal */}
                {editingVendor && (
                    <EditVendorModal
                        vendor={editingVendor}
                        isOpen={!!editingVendor}
                        onClose={handleCloseEditModal}
                    />
                )}

                {/* Import Vendors Modal */}
                <ImportVendorsModal
                    isOpen={importVendorsModalState.isOpen}
                    onClose={handleCloseImportModal}
                    onSuccess={handleImportSuccess}
                />
            </div>
        </div>
    );
}

export default VendorsPage;
